/* Gestione: quanto rende il lavoro dello studio, e quanto resta, per chi lo dirige.
 *
 * Dal 15 ottobre 2026. Risponde a tre domande: quali clienti valgono il tempo che
 * costano, quali tipi di pratica, pratiche e prestazioni rendono, come procede il lavoro
 * di ciascuno. I conti stanno nel dominio (`domain/controllo.py`, `domain/costo.py`) e qui
 * non si rifa niente: si ordina, si sceglie il periodo, si legge.
 *
 * **Il periodo si sceglie con le stesse mascherine della scheda Periodo** (dal 17 ottobre 2026):
 * anno, semestri, trimestri, anno a cavallo, date a scelta. Quanto i clienti devono ancora si
 * legge li', per periodo, e non in questa tabella.
 *
 * **Dal 23 ottobre 2026 la tabella non ha piu' la colonna "Da fatturare"** ne' l'ordine per quella
 * colonna: il lavoro fermo, per cliente e per pratica, si legge nel Periodo, dove si apre dal
 * numero. Resta il numero in testa, che qui serve a un'altra cosa: il costo gia' sostenuto su
 * quel lavoro. Si aggiungono la vista per **sede** del cliente (solo se almeno una e' compilata),
 * l'ordine per **tempo di incasso** (nei clienti) e una riga su quanto il fatturato dipende dai
 * primi clienti.
 *
 * **Solo l'amministratore la vede.** La schermata mostra produzione e margine di ogni
 * persona, ed e' per questo che chi imputa le ore non ha la voce nel menu e non si sente
 * controllato. Non e' una classifica: l'ordine di partenza e' per fatturato.
 *
 * **Il margine e' fatturato meno costo orario pieno** delle ore fatturate. Se a una persona
 * manca il costo, il margine delle sue righe resta vuoto e la pagina lo dice in cima:
 * un margine calcolato su un costo mancante sarebbe un numero plausibile e sbagliato.
 *
 * **"Rispetto al listino"** e' il fatturato diviso quanto le stesse ore varrebbero a tariffa:
 * sotto il 100% il lavoro e' stato venduto sotto listino, ed e' la colonna che dice dove si
 * perde. **"In perdita"** e' un margine negativo: il lavoro e' costato piu' di quanto e'
 * stato pagato.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { controllo } from "../api";
import { useCarica } from "../carica";
import { ModificheListino } from "../componenti/ModificheListino";
import { SelettorePeriodo } from "../componenti/SelettorePeriodo";
import { euro, oreEMinuti, somma, trova, type Gestione as DatiGestione, type RigaGestione } from "../dominio";
import { selezione, type Selezione } from "../periodi";

type Vista = "clienti" | "sedi" | "tipi" | "pratiche" | "attivita" | "persone";
type Ordine = "ricavo" | "margine" | "margine_basso" | "ore" | "euro_per_ora" | "rispetto" | "giorni_incasso";

const VISTE: { id: Vista; nome: string; colonna: string }[] = [
  { id: "clienti", nome: "Clienti", colonna: "Cliente" },
  { id: "sedi", nome: "Sedi", colonna: "Sede" },
  { id: "tipi", nome: "Tipi di pratica", colonna: "Tipo di pratica" },
  { id: "pratiche", nome: "Pratiche", colonna: "Pratica" },
  { id: "attivita", nome: "Prestazioni", colonna: "Prestazione" },
  { id: "persone", nome: "Persone", colonna: "Persona" },
];

const ORDINI: { id: Ordine; nome: string }[] = [
  { id: "ricavo", nome: "Fatturato" },
  { id: "margine", nome: "Margine, dal più alto" },
  { id: "margine_basso", nome: "Margine, dal più basso" },
  { id: "ore", nome: "Ore" },
  { id: "euro_per_ora", nome: "Euro all'ora" },
  { id: "rispetto", nome: "Rispetto al listino, dal più basso" },
  { id: "giorni_incasso", nome: "Incassa in, dal più lento" },
];

/** La chiave che il server dà al gruppo dei clienti senza la sede compilata (`SENZA_SEDE`). */
const CHIAVE_SENZA_SEDE = "-";

function numero(testo: string | null): number | null {
  return testo === null ? null : Number(testo);
}

function chiaveOrdine(r: RigaGestione, ordine: Ordine): number {
  switch (ordine) {
    case "ricavo":
      return -Number(r.ricavo);
    case "margine":
      // Senza margine (manca un costo, o niente ore fatturate) in fondo.
      return -(numero(r.margine) ?? Number.NEGATIVE_INFINITY);
    case "margine_basso":
      return numero(r.margine) ?? Number.POSITIVE_INFINITY;
    case "ore":
      return -r.minuti;
    case "euro_per_ora":
      return -(numero(r.euro_per_ora) ?? -1);
    case "rispetto":
      return numero(r.rispetto_tariffa) ?? Number.POSITIVE_INFINITY;
    case "giorni_incasso":
      // Chi non ha ancora pagato nessun avviso (nessun dato) in fondo.
      return r.giorni_incasso === null ? Number.POSITIVE_INFINITY : -r.giorni_incasso;
  }
}

function rispetto(r: RigaGestione): string {
  const x = numero(r.rispetto_tariffa);
  return x === null ? "–" : `${Math.round(x * 100)}%`;
}

function percento(x: string | null): string {
  return x === null ? "" : `${Math.round(Number(x) * 100)}%`;
}

export function Gestione() {
  const [sel, setSel] = useState<Selezione>(() => selezione("anno", new Date().getFullYear()));
  const dati = useCarica<DatiGestione>(() => controllo.gestione(undefined, sel.dal, sel.al), [sel.dal, sel.al]);
  // Gli anni offerti dalle mascherine restano quelli dell'ultima risposta mentre ne arriva una nuova,
  // cosi' il selettore non cambia sotto le dita.
  const [anni, setAnni] = useState<number[]>([sel.anno]);
  useEffect(() => {
    if (dati.dati) setAnni(dati.dati.anni);
  }, [dati.dati]);
  const [vistaScelta, setVista] = useState<Vista>("clienti");
  const [ordineScelto, setOrdine] = useState<Ordine>("ricavo");
  const [soloPerdita, setSoloPerdita] = useState(false);
  const [cerca, setCerca] = useState("");
  // Quante righe mostrare: quindici alla volta, ripartendo da quindici a ogni cambio di vista, ordine o ricerca.
  const [limite, setLimite] = useState({ chiave: "", quante: 15 });
  const g = dati.dati;
  // La vista per sede esiste solo se almeno un cliente ha la sede compilata: con tutti "Senza sede" sarebbe
  // una tabella di una riga. E l'ordine per tempo di incasso ha senso solo nei clienti: se la scelta non
  // vale piu' (nuovo periodo, altra vista) si torna a una che vale, senza lasciare la tendina su una voce nascosta.
  const haSedi = g !== undefined && g.sedi.some((r) => r.chiave !== CHIAVE_SENZA_SEDE);
  const vista: Vista = vistaScelta === "sedi" && !haSedi ? "clienti" : vistaScelta;
  const ordine: Ordine = ordineScelto === "giorni_incasso" && vista !== "clienti" ? "ricavo" : ordineScelto;

  const righe = useMemo(() => {
    if (g === undefined) return [];
    return g[vista]
      .filter((r) => !soloPerdita || r.in_perdita)
      .filter((r) => trova(cerca, r.nome))
      .sort((a, b) => chiaveOrdine(a, ordine) - chiaveOrdine(b, ordine) || a.nome.localeCompare(b.nome));
  }, [g, vista, ordine, soloPerdita, cerca]);

  const selettore = (
    <>
      <p className="aiuto scheda-domanda">Il lavoro rende? Quanto resta, per cliente, sede, tipo di pratica e persona.</p>
      <div className="filtri" aria-label="Periodo">
        <SelettorePeriodo valore={sel} anni={anni} cambia={setSel} />
      </div>
    </>
  );
  if (dati.errore) {
    return (
      <section className="gestione">
        {selettore}
        <p className="errore" role="alert">
          {dati.errore}
        </p>
      </section>
    );
  }
  if (g === undefined) {
    return (
      <section className="gestione">
        {selettore}
        <p className="attesa">Un momento.</p>
      </section>
    );
  }

  const chiaveLimite = `${vista}|${ordine}|${soloPerdita}|${cerca}|${g.dal}|${g.al}`;
  const quante = limite.chiave === chiaveLimite ? limite.quante : 15;
  const mostrate = righe.slice(0, quante);
  const t = g.totale;
  const inPerdita = g[vista].filter((r) => r.in_perdita).length;
  const persone = vista === "persone";
  // Se manca il costo di una persona il margine totale non c'e' (sarebbe sbagliato): ma chi ha il costo
  // ha il suo margine, e la loro somma e' un numero vero, dichiarato parziale, invece di un trattino.
  const conCosto = g.persone.filter((r) => r.margine !== null);
  const parziale =
    t.margine === null && conCosto.length > 0 ? somma(conCosto.map((r) => r.margine as string)) : null;
  return (
    <section className="gestione">
      {selettore}
      {g.persone_senza_costo.length > 0 && (
        <p className="riga-avviso">
          Manca il costo orario di {g.persone_senza_costo.join(", ")}: il margine del loro lavoro non è
          completo. <Link to="/impostazioni/costi">Imposta i costi</Link>.
        </p>
      )}

      <div className="numeri-testa">
        <div className="numero-testa">
          <span className="numero-testa-cifra">{euro(t.ricavo)}</span>
          <span className="numero-testa-nome">fatturato nel periodo</span>
        </div>
        <div className="numero-testa">
          <span className="numero-testa-cifra">
            {t.margine !== null ? euro(t.margine) : parziale !== null ? euro(parziale) : "–"}
          </span>
          <span className="numero-testa-nome">
            {t.margine !== null
              ? `margine${t.margine_percentuale !== null ? `, il ${percento(t.margine_percentuale)}` : ""}`
              : parziale !== null
                ? `margine parziale, senza il lavoro di ${g.persone_senza_costo.join(", ")}`
                : "margine non calcolabile: mancano i costi"}
          </span>
        </div>
        <div className="numero-testa">
          <span className="numero-testa-cifra">{oreEMinuti(t.minuti)}</span>
          <span className="numero-testa-nome">ore registrate nel periodo</span>
        </div>
        <div className="numero-testa">
          <span className="numero-testa-cifra">{euro(t.da_fatturare)}</span>
          <span className="numero-testa-nome">
            da fatturare del lavoro del periodo, costo già sostenuto {euro(t.costo_da_recuperare)}
          </span>
        </div>
      </div>

      <div className="gestione-comandi">
        <div className="gestione-viste" role="tablist" aria-label="Cosa guardare">
          {VISTE.filter((v) => v.id !== "sedi" || haSedi).map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={vista === v.id}
              className={vista === v.id ? "tasto tasto-principale" : "tasto"}
              onClick={() => setVista(v.id)}
            >
              {v.nome}
            </button>
          ))}
        </div>
        <div className="campo">
          <label htmlFor="gestione-ordine">Ordina per</label>
          <select id="gestione-ordine" value={ordine} onChange={(e) => setOrdine(e.target.value as Ordine)}>
            {ORDINI.filter((o) => o.id !== "giorni_incasso" || vista === "clienti").map((o) => (
              <option key={o.id} value={o.id}>
                {o.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="gestione-ricerca campo campo-largo">
        <label htmlFor="gestione-cerca">Cerca</label>
        <input
          id="gestione-cerca"
          type="search"
          value={cerca}
          placeholder={`un nome tra i ${g[vista].length}`}
          onChange={(e) => setCerca(e.target.value)}
        />
      </div>

      {vista === "clienti" && g.primi_clienti_quota !== null && (
        <p className="narrativa">
          I primi {g.primi_clienti_quanti} clienti valgono il {percento(g.primi_clienti_quota)} del fatturato del periodo.
        </p>
      )}

      {inPerdita > 0 && (
        <label className="gestione-perdita">
          <input type="checkbox" checked={soloPerdita} onChange={(e) => setSoloPerdita(e.target.checked)} />
          <span>
            Solo quelli in perdita ({inPerdita})
          </span>
        </label>
      )}

      {righe.length === 0 ? (
        <p className="aiuto">{soloPerdita ? "Niente in perdita." : "Nessun lavoro registrato nel periodo."}</p>
      ) : (
        <table className="tabella gestione-tabella">
          <thead>
            <tr>
              <th>{VISTE.find((v) => v.id === vista)?.colonna}</th>
              <th className="cifra colonna-secondaria">Ore</th>
              {persone && <th className="cifra colonna-secondaria">Giorni con ore</th>}
              <th className="cifra">Fatturato</th>
              <th className="cifra colonna-secondaria">Costo</th>
              <th className="cifra">Margine</th>
              {!persone && <th className="cifra colonna-secondaria">Rispetto al listino</th>}
              {vista === "clienti" && <th className="cifra colonna-secondaria">Incassa in</th>}
            </tr>
          </thead>
          <tbody>
            {mostrate.map((r) => {
              const sotto = numero(r.rispetto_tariffa) !== null && Number(r.rispetto_tariffa) < 0.85;
              return (
                <tr key={r.chiave}>
                  <td>
                    <span className="pratica-nome">{r.nome}</span>
                    {r.senza_tariffa > 0 && (
                      <span className="narrativa">
                        {r.senza_tariffa} {r.senza_tariffa === 1 ? "ora" : "ore"} senza tariffa
                      </span>
                    )}
                    {r.senza_costo > 0 && (
                      <span className="narrativa">
                        {r.senza_costo} {r.senza_costo === 1 ? "ora" : "ore"} senza costo orario
                      </span>
                    )}
                  </td>
                  <td className="cifra colonna-secondaria">{oreEMinuti(r.minuti)}</td>
                  {persone && <td className="cifra colonna-secondaria">{r.giorni_con_ore}</td>}
                  <td className="cifra">{euro(r.ricavo)}</td>
                  <td className="cifra colonna-secondaria">{r.minuti_fatturati > 0 ? euro(r.costo) : "–"}</td>
                  <td className={`cifra${r.in_perdita ? " sotto-listino" : ""}`}>
                    {r.margine === null ? "–" : euro(r.margine)}
                    {r.margine !== null && r.margine_percentuale !== null && (
                      <span className="narrativa">{percento(r.margine_percentuale)}</span>
                    )}
                    {r.in_perdita && <span className="narrativa">in perdita</span>}
                  </td>
                  {!persone && (
                    <td className={`cifra colonna-secondaria${sotto ? " sotto-listino" : ""}`}>
                      {rispetto(r)}
                      {sotto && <span className="narrativa">sotto listino</span>}
                    </td>
                  )}
                  {vista === "clienti" && (
                    <td className="cifra colonna-secondaria">{r.giorni_incasso === null ? "–" : `${r.giorni_incasso} gg`}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {righe.length > quante && (
        <p>
          <button
            type="button"
            className="tasto"
            onClick={() => setLimite({ chiave: chiaveLimite, quante: quante + 15 })}
          >
            Mostra altri 15 ({righe.length - quante} ancora nascosti)
          </button>
        </p>
      )}

      <p className="narrativa gestione-nota">
        Margine = fatturato meno il costo orario pieno delle ore fatturate. I costi si impostano in Impostazioni.
      </p>

      <ModificheListino />
    </section>
  );
}
